import { cloneElement, isValidElement } from "react"
import { matchPath } from "react-router-dom"
import { normalizeRoutePath } from "../../../Routes/routeMatching"

// Normalize route values so matching is stable across slash formatting differences.
function normalizePath(path) {
  const normalizedPath = normalizeRoutePath(path)
  return normalizedPath.length > 1
    ? normalizedPath.replace(/\/+$/, "")
    : normalizedPath
}

// Supports exact matching first, then optional section-level matching when allowed.
function isPathActive(path, currentPath, allowPartial) {
  const normalizedTargetPath = normalizePath(path)

  if (!normalizedTargetPath) {
    return false
  }

  if (currentPath === normalizedTargetPath) {
    return true
  }

  if (!allowPartial) {
    return false
  }

  // Root must only match exact '/'; otherwise every path would appear active.
  if (normalizedTargetPath === "/") {
    return false
  }

  return Boolean(
    matchPath(
      { path: normalizedTargetPath, end: false },
      currentPath,
    ),
  )
}

/**
 * Build a PrimeReact PanelMenu model and propagate active-state to ancestor nodes
 * so active branches can render expanded.
 */
export function buildPanelMenuModel({
  items = [],
  pathname,
  navigate,
  itemTemplate,
  activeClassName,
  // Allows callers to inject dynamic placeholder resolution (e.g. {organization_id}).
  resolvePath = (path) => path,
  // By default, top-level items can stay active for nested routes; deeper levels are exact.
  allowPartialAtDepth = (depth) => depth === 0,
  // Callers can prune or hide specific items without rewriting mapping logic.
  includeItem = () => true,
  // Optional hook for side effects tied to leaf navigation (e.g. localStorage updates).
  onLeafCommand,
}) {
  const normalizedCurrentPath = normalizePath(pathname)

  const mapItems = (menuItems = [], depth = 0) => {
    let hasActiveInBranch = false

    const mappedItems = (menuItems || [])
      .filter(Boolean)
      .map((item) => {
        // Evaluate inclusion per node so rules can vary by depth or item metadata.
        if (!includeItem({ item, depth })) {
          return null
        }

        const childItems = Array.isArray(item?.items) ? item.items : []
        const hasSubMenu = childItems.length > 0
        // Resolve once here so both active checks and click navigation use the same URL.
        const resolvedPath = item?.path
          ? resolvePath(item.path, item, depth)
          : null

        const mappedItem = {
          label: item?.label,
          materialIcon: item?.icon,
          template: itemTemplate,
        }

        if (hasSubMenu) {
          const {
            mappedItems: nestedItems,
            hasActiveInBranch: nestedActive,
          } = mapItems(childItems, depth + 1)

          // Parent nodes only control expansion; leaf nodes handle navigation.
          mappedItem.items = nestedItems
          mappedItem.expanded = nestedActive

          // Bubble child active state up so ancestor branches stay expanded.
          if (nestedActive) {
            hasActiveInBranch = true
          }

          return mappedItem
        }

        if (resolvedPath) {
          // Use caller-provided depth strategy to decide exact vs partial active checks.
          const isActive = isPathActive(
            resolvedPath,
            normalizedCurrentPath,
            allowPartialAtDepth(depth),
          )

          // Leaf nodes are navigable entries in the rendered PanelMenu model.
          mappedItem.command = () => {
            if (typeof onLeafCommand === "function") {
              onLeafCommand({
                item,
                depth,
                path: resolvedPath,
                isActive,
              })
            }
            navigate(resolvedPath)
          }
          mappedItem.className = isActive ? activeClassName : undefined

          if (isActive) {
            hasActiveInBranch = true
          }
        } else if (typeof item?.command === "function") {
          // Support command-only leaf entries (for actions that do not navigate).
          mappedItem.command = item.command
        }

        return mappedItem
      })
      .filter(Boolean)

    return {
      mappedItems,
      hasActiveInBranch,
    }
  }

  return mapItems(items).mappedItems
}

/**
 * Keep PrimeReact's native link element and only prepend a Material icon when provided.
 */
export function renderMaterialIconMenuItemTemplate(item, options) {
  if (!item?.materialIcon || !isValidElement(options.element)) {
    return options.element
  }

  return cloneElement(
    options.element,
    {},
    <>
      <span className="material-icons p-menuitem-icon">{item.materialIcon}</span>
      {options.element.props.children}
    </>,
  )
}
